import React, { createContext, useContext, useEffect, useRef, useState } from "react";

export interface LayoutSlots {
  infobarSlot?: React.ReactNode;
  returnSlot?: React.ReactNode;
  subheaderSlot?: React.ReactNode;
  tabsSlot?: React.ReactNode;
  toolbarSlot?: React.ReactNode;
  previewPaneSlot?: React.ReactNode;
}

interface LayoutSlotActions {
  register: (name: keyof LayoutSlots, content: React.ReactNode) => void;
  unregister: (name: keyof LayoutSlots) => void;
}

// Separate contexts: actions (stable) vs state (changes when slots update).
// Page components only subscribe to actions → no re-render when slots change.
// Layout components subscribe to state → re-render when slots change.
const LayoutSlotActionsContext = createContext<LayoutSlotActions>({
  register: () => undefined,
  unregister: () => undefined,
});

const LayoutSlotStateContext = createContext<LayoutSlots>({});

export const LayoutSlotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slots, setSlots] = useState<LayoutSlots>({});

  const actions = useRef<LayoutSlotActions>({
    register: (name, content) => setSlots((prev) => ({ ...prev, [name]: content })),
    unregister: (name) =>
      setSlots((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      }),
  }).current;

  return (
    <LayoutSlotActionsContext.Provider value={actions}>
      <LayoutSlotStateContext.Provider value={slots}>{children}</LayoutSlotStateContext.Provider>
    </LayoutSlotActionsContext.Provider>
  );
};

export const useLayoutSlots = () => useContext(LayoutSlotStateContext);

/**
 * Call inside a page component to inject content into a named layout slot.
 * The slot is automatically cleared when the component unmounts.
 *
 * Uses LayoutSlotActionsContext (stable, never changes) so the calling component
 * does NOT re-render when slot state changes — breaking the infinite loop.
 *
 * @example
 * useRegisterLayoutSlot("returnSlot", <ReturnArrow onClick={() => navigate(-1)} />);
 * useRegisterLayoutSlot("subheaderSlot", <Subheader title="Asset Details" />);
 */
export const useRegisterLayoutSlot = (name: keyof LayoutSlots, content: React.ReactNode) => {
  const { register, unregister } = useContext(LayoutSlotActionsContext);

  // Unregister on unmount
  useEffect(() => {
    return () => unregister(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, unregister]);

  // Re-register on every render to pick up async content changes.
  // Safe: this component subscribes only to LayoutSlotActionsContext (stable),
  // not LayoutSlotStateContext, so no infinite re-render loop.
  useEffect(() => {
    register(name, content);
  });
};
